import { useDraggable } from '@dnd-kit/core';
import { Group, Text } from '@mantine/core';
import { formatDuration } from '@reditelji/shared';
import type { PoolVolunteer, TimelinePayload } from '../api/types';
import { useUi } from '../state/ui';
import { DayStrip } from './DayStrip';
import { initials, statusKey } from './derive';

/**
 * A person in the pool: dragged onto a row to place them, clicked (or Enter /
 * Space) to look at the board through them — the lens (SPEC §1.1B). Clicking
 * the person the board already looks through ends it.
 */
export function DraggableVolunteerCard({
  volunteer,
  data,
  dot,
  size = 'card',
  action,
}: {
  volunteer: PoolVolunteer;
  data: TimelinePayload;
  dot: string;
  /** The lens panel's own card draws the day larger, with hour ticks. */
  size?: 'card' | 'lens';
  action?: React.ReactNode;
}) {
  const { lensVolunteerId, setLens } = useUi();
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `vol:${volunteer.id}`,
    data: { volunteerId: volunteer.id, name: volunteer.name },
  });
  const on = lensVolunteerId === volunteer.id;
  const toggle = () => setLens(on ? null : volunteer.id);
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      // After the spread: dnd-kit's own aria-pressed says whether it is being dragged.
      aria-pressed={on}
      aria-label={`${volunteer.name} — ${on ? 'končaj pogled na tablo' : 'pokaži na tabli, kam lahko gre'}`}
      // dnd-kit swallows the click that ends a drag, so this is a plain click.
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
        e.preventDefault();
        toggle();
      }}
    >
      <VolunteerCard
        volunteer={volunteer}
        dragging={isDragging}
        lens={on}
        dot={dot}
        action={action}
        strip={<DayStrip volunteer={volunteer} data={data} size={size} />}
      />
    </div>
  );
}

export function VolunteerCard({
  volunteer: v,
  action,
  dragging,
  lens,
  note,
  dot,
  out,
  strip,
}: {
  volunteer: PoolVolunteer;
  action?: React.ReactNode;
  dragging?: boolean;
  /** The board is looking through this person. */
  lens?: boolean;
  /** A line in place of the person's day, for somebody with no day to draw. */
  note?: string;
  /** The person's day (`DayStrip`). */
  strip?: React.ReactNode;
  /** The dot's status key; the person's own status when not given. */
  dot?: string;
  /** Not coming (SPEC §1.1K): marked red, and no hours to weigh. */
  out?: boolean;
}) {
  const loadColor =
    v.dayMinutes === 0
      ? 'oklch(0.55 0.08 155)'
      : v.dayMinutes >= 10 * 60
        ? 'oklch(0.55 0.12 25)'
        : undefined;
  return (
    <div
      className={`tl-pool-card${dragging ? ' tl-pool-card--dragging' : ''}${lens ? ' tl-pool-card--lens' : ''}${out ? ' tl-pool-card--out' : ''}`}
    >
      <Group gap={8} wrap="nowrap">
        <div className="tl-avatar">{initials(v.name)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <span className={`tl-status-dot tl-status-dot--${dot ?? statusKey(v)}`} />
            <Text size="sm" fw={600} truncate style={out ? { color: 'oklch(0.42 0.12 25)' } : undefined}>
              {v.name}
            </Text>
            {out ? null : (
              <Text
                size="xs"
                style={{ fontFamily: 'var(--rr-font-mono)', marginLeft: 'auto', color: loadColor }}
              >
                {v.dayMinutes > 0 ? formatDuration(v.dayMinutes) : '0 h'}
              </Text>
            )}
          </Group>
          {note ? (
            <Text size="xs" c={out ? undefined : 'dimmed'} truncate style={out ? { color: 'oklch(0.55 0.08 25)' } : undefined}>
              {note}
            </Text>
          ) : (
            strip
          )}
        </div>
        {action}
      </Group>
    </div>
  );
}
